import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Minus, Plus, ChevronLeft, ChevronRight, Languages } from "lucide-react";

const btn = "flex h-9 w-9 items-center justify-center border border-line text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink";

export default function ReaderMode({ open, onClose, title, verses = [], onPrev, onNext, hasPrev = false, hasNext = false, pageLabel }) {
  const [size, setSize] = useState(32);
  const [showTrans, setShowTrans] = useState(true);
  const scrollRef = useRef(null);
  const hasNav = !!(onPrev || onNext);
  const hasTrans = verses.some((v) => v.iast);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev && onPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext && onNext) onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, onPrev, onNext, hasPrev, hasNext]);

  // On chapter change (or opening), jump the reader to the start of the content.
  const chapterSig = `${title}|${pageLabel}|${verses.length}|${verses[0]?.num || ""}|${(verses[0]?.body || "").slice(0, 24)}`;
  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: "auto" });
  }, [open, chapterSig]);

  if (!open) return null;

  return createPortal(
    <div ref={scrollRef} className="fixed inset-0 z-[200] overflow-y-auto bg-[var(--parchment)]" data-testid="reader-mode">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-line bg-[var(--parchment)]/92 px-5 py-3 backdrop-blur-md md:px-8">
        <p className="truncate font-serif-latin text-lg text-ink">{title}</p>
        <div className="flex items-center gap-2">
          {hasTrans && (
            <button data-testid="reader-trans-toggle" aria-label="Toggle transliteration" onClick={() => setShowTrans((s) => !s)}
              className={`${btn} ${showTrans ? "border-saffron text-saffron" : ""}`} title="Transliteration"><Languages className="h-4 w-4" /></button>
          )}
          <button data-testid="reader-font-dec" aria-label="Smaller text" onClick={() => setSize((s) => Math.max(20, s - 3))} className={btn}><Minus className="h-4 w-4" /></button>
          <span className="w-9 text-center text-xs text-ink-3">{size}px</span>
          <button data-testid="reader-font-inc" aria-label="Larger text" onClick={() => setSize((s) => Math.min(60, s + 3))} className={btn}><Plus className="h-4 w-4" /></button>
          <button data-testid="reader-close" aria-label="Close reader" onClick={onClose} className={`${btn} ml-2`}><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-5 py-14 text-center md:py-24">
        {verses.map((v, i) => (
          <div key={i} className="mb-12">
            <p className="whitespace-pre-line font-deva tracking-wide text-ink" style={{ fontSize: `${size}px`, lineHeight: 2.15 }}>{v.body}</p>
            {showTrans && v.iast && <p className="mx-auto mt-3 max-w-[60ch] whitespace-pre-line font-serif-latin italic text-ink-2" style={{ fontSize: `${Math.round(size * 0.5)}px`, lineHeight: 1.7 }}>{v.iast}</p>}
            {v.num && <p className="mt-4 font-deva text-saffron" style={{ fontSize: `${Math.round(size * 0.62)}px` }}>॥&nbsp;{v.num}&nbsp;॥</p>}
          </div>
        ))}
      </div>

      {hasNav && (
        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-4 border-t border-line bg-[var(--parchment)]/92 px-5 py-3 backdrop-blur-md md:px-8">
          <button
            data-testid="reader-prev-chapter"
            disabled={!hasPrev}
            onClick={() => hasPrev && onPrev?.()}
            className="inline-flex items-center gap-2 text-sm text-ink-2 transition-colors hover:text-saffron disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" /> Previous Chapter
          </button>
          {pageLabel && <span className="text-xs text-ink-3">{pageLabel}</span>}
          <button
            data-testid="reader-next-chapter"
            disabled={!hasNext}
            onClick={() => hasNext && onNext?.()}
            className="inline-flex items-center gap-2 text-sm text-ink-2 transition-colors hover:text-saffron disabled:opacity-30"
          >
            Next Chapter <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}
